import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Award, Calendar } from 'lucide-react';
import api from '../services/api';

const CourseCard = ({ course }) => {
    return (
        <div className="bg-brand-card rounded-2xl border border-gray-800 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col h-full relative group overflow-hidden">
            {/* Image Area - Full Width */}
            <div className="w-full h-48 bg-brand-card relative overflow-hidden">
                {course.logo ? (
                    <img
                        src={course.logo}
                        alt={course.university}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center text-gray-400 font-bold">IMAGE</div>' }}
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 font-bold">IMAGE</div>
                )}

                {/* Badge Overlay */}
                {course.badge && (
                    <div className={`absolute top-4 right-4 ${course.badgeColor || 'bg-blue-600'} text-black text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow-sm`}>
                        {course.badge}
                    </div>
                )}
            </div>

            {/* Content Area */}
            <div className="p-6 flex flex-col flex-grow">
                {/* University Name */}
                <p className="text-gray-500 text-xs font-medium mb-1">{course.university}</p>

                {/* Course Title */}
                <h3 className="text-lg font-bold text-brand-textLight mb-0 leading-snug min-h-[56px]">
                    {course.title}
                </h3>

                {/* Tags */}
                <div className="mb-4 flex flex-wrap gap-2">
                    {course.tags && course.tags.map((tag, i) => (
                        <span key={i} className="inline-block bg-brand-gold text-black-600 text-[10px] font-semibold px-2 py-1 rounded">
                            {tag}
                        </span>
                    ))}
                </div>

                {/* Meta Info */}
                <div className="mt-auto space-y-3 mb-6">
                    <div className="flex items-center gap-2 text-brand-textMuted">
                        <Award className="w-4 h-4 text-gray-400" />
                        <span className="text-xs font-medium">{course.type}</span>
                    </div>
                    <div className="flex items-center gap-2 text-brand-textMuted">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-xs font-medium">{course.duration}</span>
                    </div>
                </div>

                {/* Actions */}
                <div className="mt-2">
                    {course.programUrl && (course.programUrl.startsWith('http') || course.programUrl.startsWith('https')) ? (
                        <a
                            href={course.programUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center w-full py-2.5 px-4 rounded-lg bg-brand-gold text-black font-bold text-sm hover:bg-brand-goldHover transition-colors shadow-sm text-center"
                        >
                            View Program
                        </a>
                    ) : (
                        <Link
                            to={course.programUrl || '#'}
                            className="flex items-center justify-center w-full py-2.5 px-4 rounded-lg bg-brand-gold text-black font-bold text-sm hover:bg-brand-goldHover transition-colors shadow-sm text-center"
                        >
                            View Program
                        </Link>
                    )}
                </div>
            </div>
        </div>
    );
};

const CoursesSection = () => {
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const scrollContainerRef = React.useRef(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(true);

    useEffect(() => {
        const fetchCourses = async () => {
            try {
                const response = await api.get('/programs');
                setCourses(response.data || []);
            } catch (error) {
                console.error("Failed to fetch programs:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchCourses();
    }, []);

    const checkScroll = () => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
            setShowLeftArrow(scrollLeft > 0);
            // Allow a small buffer (10px) for float/rounding differences
            setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
        }
    };

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', checkScroll);
            // Initial check
            checkScroll();
            // Check on resize
            window.addEventListener('resize', checkScroll);
        }
        return () => {
            if (container) {
                container.removeEventListener('scroll', checkScroll);
            }
            window.removeEventListener('resize', checkScroll);
        };
    }, [courses]);

    const scroll = (direction) => {
        if (scrollContainerRef.current) {
            const { clientWidth } = scrollContainerRef.current;
            const scrollAmount = direction === 'left' ? -clientWidth : clientWidth;
            scrollContainerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
    };

    if (loading) {
        return (
            <div className="bg-brand-bg py-20 flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-gold"></div>
            </div>
        );
    }

    // Fallback if no courses
    if (courses.length === 0) {
        return null;
    }

    return (
        <div className="bg-brand-bg pt-20 pb-8">
            <div className="max-w-7xl mx-auto px-6 relative group/section">
                {/* Header */}
                <div className="mb-8 md:mb-12 flex justify-between items-end gap-4">
                    <div className="max-w-[75%] md:max-w-none">
                        <span className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 block">
                            Trending Courses
                        </span>
                        <h2 className="text-2xl md:text-3xl font-bold text-brand-textLight leading-tight ">
                            <span className="text-3xl font-bold text-brand-textMuted">Explore our</span> <span className="text-brand-gold">most popular programs</span>
                        </h2>
                    </div>

                    {/* Navigation Buttons */}
                    <div className="flex gap-2 shrink-0 pb-1">
                        <button
                            onClick={() => scroll('left')}
                            disabled={!showLeftArrow}
                            className={`p-2.5 md:p-3 rounded-full border border-gray-200 transition-all ${!showLeftArrow
                                ? 'opacity-30 cursor-not-allowed bg-gray-50 text-gray-400'
                                : 'hover:bg-brand-gold hover:text-black hover:border-brand-gold bg-brand-card text-brand-textLight shadow-sm'
                                }`}
                            aria-label="Scroll left"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
                        </button>
                        <button
                            onClick={() => scroll('right')}
                            disabled={!showRightArrow}
                            className={`p-2.5 md:p-3 rounded-full border border-gray-200 transition-all ${!showRightArrow
                                ? 'opacity-30 cursor-not-allowed bg-gray-50 text-gray-400'
                                : 'hover:bg-brand-gold hover:text-black hover:border-brand-gold bg-brand-card text-brand-textLight shadow-sm'
                                }`}
                            aria-label="Scroll right"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
                        </button>
                    </div>
                </div>

                {/* Carousel Container */}
                <div
                    ref={scrollContainerRef}
                    className="flex overflow-x-auto gap-6 pb-8 snap-x snap-mandatory scrollbar-hide -mx-6 px-6 md:mx-0 md:px-0"
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                    {courses.map(course => (
                        <div
                            key={course._id || course.id}
                            className="min-w-[85%] sm:min-w-[calc(50%-12px)] lg:min-w-[calc(33.333%-16px)] snap-start flex-shrink-0 h-full"
                        >
                            <CourseCard course={course} />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default CoursesSection;
